import { useContext, useState } from "react";
import { contactInformation } from "../constants.js";
import { SiteContentContext } from "../context/SiteContentContext";
import { contact } from "../httpService";
import Button from "./button.jsx";
import ContentText from "./contentText.jsx";
import SectionContent from "./sectionContent.jsx";
import { OFFICES, SwedenMap } from "./swedenMap.jsx";

export default function ContactSection({ forceStacked = false }) {
  const { getContent } = useContext(SiteContentContext);
  const phone = getContent("company.phone", contactInformation.telephoneNumber);
  const email = getContent("company.email", contactInformation.email);
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", message: "" });
  const [status, setStatus] = useState(null);
  const [sending, setSending] = useState(false);

  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!(form.name.trim() && form.email.trim() && form.message.trim() && form.phone.trim())) {
      return;
    }

    setSending(true);
    setStatus(null);
    try {
      await contact({
        replyTo: form.email,
        subject: `Kontaktförfrågan från ${form.name}${form.company ? `, ${form.company}` : ""}${form.phone ? ` (${form.phone})` : ""}`,
        text: form.message,
      });
      setStatus("success");
      setForm({ name: "", company: "", email: "", phone: "", message: "" });
    } catch {
      setStatus("error");
    } finally {
      setSending(false);
    }
  }

  return (
    <section className="relative overflow-hidden flex-1">
      {/* Full-bleed split background: blue left, grey right */}
      {!forceStacked && (
        <div className="absolute inset-0 hidden lg:flex" aria-hidden="true">
          <div className="w-[50%] bg-primary" />
          <div className="w-[50%] bg-white" />
        </div>
      )}

      <SectionContent className={`relative !px-0${!forceStacked ? " lg:!px-8" : ""}`}>
        <div className={`grid grid-cols-1${!forceStacked ? " lg:grid-cols-[5fr_6fr]" : ""}`}>
          {/* Left — map + addresses (dark blue) */}
          <div className="bg-primary px-6 py-12 md:py-16 text-white flex flex-col gap-6 items-center text-center">
            <h2 className="text-3xl md:text-4xl font-bold">
              <ContentText contentKey="contact.heading" fallback="Fler frågor?" />
            </h2>
            <p className="text-white/80">
              <ContentText
                contentKey="contact.subheading"
                fallback="Kontakta oss så hjälper vi dig vidare!"
              />
            </p>

            <div className="flex-1 flex items-center justify-center gap-12">
              <div className="flex flex-col gap-4 text-left">
                {OFFICES.map((office) => {
                  const postalCode = getContent(`office.${office.key}.postal_code`, "");
                  const city = getContent(`office.${office.key}.city`, office.city);
                  return (
                    <div key={office.key}>
                      <p className="font-semibold text-base">
                        <ContentText
                          contentKey={`office.${office.key}.city`}
                          fallback={office.city}
                        />
                      </p>
                      <p className="text-primary200 text-sm">
                        <ContentText
                          contentKey={`office.${office.key}.street`}
                          fallback={office.street}
                        />
                      </p>
                      {postalCode && (
                        <p className="text-primary200 text-sm">
                          {postalCode} {city}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="max-w-[220px]">
                <SwedenMap />
              </div>
            </div>

            <div className="pt-4 border-t border-white/20 space-y-1 text-sm">
              <p>
                📞{" "}
                <a href={`tel:${phone}`} className="hover:underline">
                  {phone}
                </a>
              </p>
              <p>
                ✉️{" "}
                <a href={`mailto:${email}`} className="hover:underline">
                  {email}
                </a>
              </p>
            </div>
          </div>

          {/* Right — contact form */}
          <div
            className={`bg-white px-8${!forceStacked ? " lg:pl-24" : ""} py-12 md:py-16 flex flex-col justify-center items-center`}
          >
            <form
              onSubmit={handleSubmit}
              className="flex flex-col gap-4 w-full max-w-xl"
              data-testid="contact-form"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input
                  type="text"
                  name="name"
                  placeholder={getContent("contact.name_placeholder", "Namn")}
                  value={form.name}
                  onChange={handleChange}
                  required
                  className="border border-border-default rounded px-4 py-3 bg-surface-muted focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="contact-name"
                />
                <input
                  type="email"
                  name="email"
                  placeholder={getContent("contact.email_placeholder", "E-post")}
                  value={form.email}
                  onChange={handleChange}
                  required
                  className="border border-border-default rounded px-4 py-3 bg-surface-muted focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="contact-email"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input
                  type="text"
                  name="company"
                  placeholder={getContent("contact.company_placeholder", "Företag")}
                  value={form.company}
                  onChange={handleChange}
                  className="border border-border-default rounded px-4 py-3 bg-surface-muted focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="contact-company"
                />
                <input
                  type="tel"
                  name="phone"
                  placeholder={getContent("contact.phone_placeholder", "Telefon")}
                  value={form.phone}
                  onChange={handleChange}
                  required
                  className="border border-border-default rounded px-4 py-3 bg-surface-muted focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="contact-phone"
                />
              </div>
              <textarea
                name="message"
                placeholder={getContent("contact.message_placeholder", "Meddelande")}
                rows={5}
                value={form.message}
                onChange={handleChange}
                required
                className="border border-border-default rounded px-4 py-3 bg-surface-muted focus:outline-none focus:ring-2 focus:ring-primary resize-y"
                data-testid="contact-message"
              />
              <Button
                type="submit"
                size="pill"
                loading={sending}
                sx="self-center"
                data-testid="contact-submit"
              >
                {sending ? (
                  <ContentText contentKey="contact.sending" fallback="Skickar..." />
                ) : (
                  <ContentText contentKey="contact.submit_button" fallback="Skicka" />
                )}
              </Button>
              {status === "success" && (
                <p className="text-green-600 text-sm text-center">
                  <ContentText
                    contentKey="contact.success_message"
                    fallback="Meddelandet har skickats!"
                  />
                </p>
              )}
              {status === "error" && (
                <p className="text-red-600 text-sm text-center">
                  <ContentText
                    contentKey="contact.error_message"
                    fallback="Något gick fel. Försök igen."
                  />
                </p>
              )}
            </form>
          </div>
        </div>
      </SectionContent>
    </section>
  );
}
